import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createAgent, getAgent, updateAgent, clearAgent, uploadAgentDocument } from '../../actions/agent';
import { 
    ArrowLeft, 
    Save, 
    Upload, 
    User, 
    MapPin, 
    CreditCard, 
    FileText, 
    Clock,
    Check,
    ChevronRight,
    Phone,
    Mail,
    Home,
    Camera
} from 'lucide-react';

const initialState = {
    name: '',
    phone: '',
    permanentAddress: '',
    localAddress: '',
    bankDetails: {
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: ''
    },
    photo: '',
    drivingLicenseNumber: '',
    emergencyContactNumber: '',
    documents: {
        agentPhoto: '',
        drivingLicenseFront: '',
        drivingLicenseBack: '',
        aadhaarCardFront: '',
        aadhaarCardBack: ''
    },
    isActive: false,
    currentLocation: {
        type: 'Point',
        coordinates: [0, 0],
        lastUpdated: new Date(),
        timestamp: Date.now(),
        accuracy: 0
    },
    workingHours: {
        monday: { start: '09:00', end: '18:00' },
        tuesday: { start: '09:00', end: '18:00' },
        wednesday: { start: '09:00', end: '18:00' },
        thursday: { start: '09:00', end: '18:00' },
        friday: { start: '09:00', end: '18:00' },
        saturday: { start: '09:00', end: '18:00' },
        sunday: { start: '09:00', end: '18:00' }
    },
    boundaryType: 'fixed',
    status: 'offline',
    isApproved: false,
};

const AgentOnboarding = ({
    createAgent,
    getAgent,
    updateAgent,
    clearAgent,
    uploadAgentDocument,
    agent,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [documentPreviews, setDocumentPreviews] = useState({});

    const isNewAgent = id === 'new';
    const totalSteps = 5;

    const steps = [
        { number: 1, title: 'Personal Information', icon: User, description: 'Basic details and contact information' },
        { number: 2, title: 'Address Details', icon: MapPin, description: 'Permanent and local addresses' },
        { number: 3, title: 'Bank Information', icon: CreditCard, description: 'Banking details for payments' },
        { number: 4, title: 'Documents', icon: FileText, description: 'Upload required documents' },
        { number: 5, title: 'Working Hours', icon: Clock, description: 'Set availability schedule' }
    ];

    useEffect(() => {
        if (!isNewAgent && (!agent || agent._id !== id)) {
            getAgent(id);
        } else if (!isNewAgent && agent && agent._id === id) {
            setFormData({ ...initialState, ...agent });
            
            // Set previews for existing documents that are images
            if (agent.documents) {
                const previews = {};
                Object.keys(agent.documents).forEach(docType => {
                    if (agent.documents[docType] && 
                        (agent.documents[docType].toLowerCase().includes('.jpg') || 
                         agent.documents[docType].toLowerCase().includes('.jpeg') || 
                         agent.documents[docType].toLowerCase().includes('.png'))) {
                        // For existing documents, use the full server path
                        previews[docType] = `${process.env.REACT_APP_SERVER_PUBLIC_PATH}/${agent.documents[docType]}`;
                    }
                });
                setDocumentPreviews(previews);
            }
        } else if (isNewAgent) {
            clearAgent();
            setFormData(initialState);
            setDocumentPreviews({});
        }
    }, [id, isNewAgent, agent, getAgent, clearAgent]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleWorkingHoursChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            workingHours: {
                ...prev.workingHours,
                [day]: {
                    ...prev.workingHours[day],
                    [field]: value
                }
            }
        }));
    };

    const handleFileUpload = async (e, documentType) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            setErrors(prev => ({
                ...prev,
                [documentType]: 'Please upload only JPEG, PNG, or PDF files'
            }));
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                [documentType]: 'File size must be less than 5MB'
            }));
            return;
        }

        try {
            setUploadingFiles(prev => ({ ...prev, [documentType]: true }));
            setErrors(prev => ({ ...prev, [documentType]: '' }));

            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setDocumentPreviews(prev => ({
                        ...prev,
                        [documentType]: e.target.result
                    }));
                };
                reader.readAsDataURL(file);
            }
            // Upload file
            const payload = {
                'file': file,
                'documentType': documentType,
                'agent_id': id
            }

            const result = await uploadAgentDocument(payload);
            
            const filePath = result.data.filePath || result.data.filename;
            setFormData(prev => ({
                ...prev,
                documents: {
                    ...prev.documents,
                    [documentType]: filePath
                }
            }));

            // If the uploaded file is an image and we don't have a preview from FileReader, set server preview
            if (!documentPreviews[documentType] && file.type.startsWith('image/')) {
                setDocumentPreviews(prev => ({
                    ...prev,
                    [documentType]: `${process.env.REACT_APP_SERVER_PUBLIC_PATH}/${filePath}`
                }));
            }

        } catch (error) {
            setErrors(prev => ({
                ...prev,
                [documentType]: 'Failed to upload file. Please try again.'
            }));
        } finally {
            setUploadingFiles(prev => ({ ...prev, [documentType]: false }));
        }
    };

    const validateStep = (step) => {
        const newErrors = {};

        switch (step) {
            case 1:
                if (!formData.name.trim()) newErrors.name = 'Name is required';
                if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
                if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
                    newErrors.phone = 'Phone number must be 10 digits';
                }
                if (!formData.drivingLicenseNumber.trim()) {
                    newErrors.drivingLicenseNumber = 'Driving license number is required';
                }
                if (formData.emergencyContactNumber && !/^\d{10}$/.test(formData.emergencyContactNumber)) {
                    newErrors.emergencyContactNumber = 'Emergency contact must be 10 digits';
                }
                break;

            case 2:
                if (!formData.permanentAddress.trim()) {
                    newErrors.permanentAddress = 'Permanent address is required';
                }
                break;

            case 3:
                if (formData.bankDetails.accountNumber || formData.bankDetails.ifscCode || 
                    formData.bankDetails.accountHolderName || formData.bankDetails.bankName) {
                    if (!formData.bankDetails.accountNumber.trim()) {
                        newErrors['bankDetails.accountNumber'] = 'Account number is required';
                    }
                    if (!formData.bankDetails.ifscCode.trim()) {
                        newErrors['bankDetails.ifscCode'] = 'IFSC code is required';
                    }
                    if (!formData.bankDetails.accountHolderName.trim()) {
                        newErrors['bankDetails.accountHolderName'] = 'Account holder name is required';
                    }
                    if (!formData.bankDetails.bankName.trim()) {
                        newErrors['bankDetails.bankName'] = 'Bank name is required';
                    }
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            // Move to next step without submitting form
            setCurrentStep(prev => Math.min(prev + 1, totalSteps));
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        // Validate all steps
        let isValid = true;
        for (let step = 1; step <= totalSteps; step++) {
            if (!validateStep(step)) {
                isValid = false;
            }
        }

        if (!isValid) {
            setCurrentStep(1); // Go back to first step with errors
            return;
        }

        try {
            setIsSubmitting(true);
            
            if (isNewAgent) {
                await createAgent(formData);
                navigate('/inventory/v1.2/agents');
            } else {
                await updateAgent(agent._id, formData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStepIndicator = () => (
        <div className="row mb-4">
            <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                    {steps.map((step, index) => (
                        <div key={step.number} className="d-flex align-items-center">
                            <div className={`step-indicator ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}>
                                <div className="step-circle">
                                    {currentStep > step.number ? (
                                        <Check size={16} />
                                    ) : (
                                        <step.icon size={16} />
                                    )}
                                </div>
                                <div className="step-info">
                                    <div className="step-title">{step.title}</div>
                                    <div className="step-description">{step.description}</div>
                                </div>
                            </div>
                            {index < steps.length - 1 && (
                                <ChevronRight className="step-separator" size={20} />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderPersonalInformation = () => (
        <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
                <h5 className="mb-0 d-flex align-items-center">
                    <User size={20} className="me-2" />
                    Personal Information
                </h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Full Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            placeholder="Enter full name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Phone Number *</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <Phone size={16} />
                            </span>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                placeholder="Enter 10-digit phone number"
                                maxLength="10"
                            />
                            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Driving License Number *</label>
                        <input
                            type="text"
                            name="drivingLicenseNumber"
                            value={formData.drivingLicenseNumber}
                            onChange={handleChange}
                            className={`form-control ${errors.drivingLicenseNumber ? 'is-invalid' : ''}`}
                            placeholder="Enter driving license number"
                        />
                        {errors.drivingLicenseNumber && <div className="invalid-feedback">{errors.drivingLicenseNumber}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Emergency Contact Number</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <Phone size={16} />
                            </span>
                            <input
                                type="tel"
                                name="emergencyContactNumber"
                                value={formData.emergencyContactNumber}
                                onChange={handleChange}
                                className={`form-control ${errors.emergencyContactNumber ? 'is-invalid' : ''}`}
                                placeholder="Enter 10-digit emergency contact"
                                maxLength="10"
                            />
                            {errors.emergencyContactNumber && <div className="invalid-feedback">{errors.emergencyContactNumber}</div>}
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="offline">Offline</option>
                            <option value="online">Online</option>
                            <option value="busy">Busy</option>
                            <option value="on_delivery">On Delivery</option>
                            <option value="on_refill">On Refill</option>
                            <option value="low_inventory">Low Inventory</option>
                        </select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Boundary Type</label>
                        <select
                            name="boundaryType"
                            value={formData.boundaryType}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="fixed">Fixed</option>
                            <option value="floating">Floating</option>
                        </select>
                    </div>

                    <div className="col-12 mb-3">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                        className="form-check-input"
                                        id="isActive"
                                    />
                                    <label className="form-check-label" htmlFor="isActive">
                                        Agent is currently active
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        name="isApproved"
                                        checked={formData.isApproved}
                                        onChange={handleChange}
                                        className="form-check-input"
                                        id="isApproved"
                                    />
                                    <label className="form-check-label" htmlFor="isApproved">
                                        Agent is approved for operations
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAddressDetails = () => (
        <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
                <h5 className="mb-0 d-flex align-items-center">
                    <MapPin size={20} className="me-2" />
                    Address Information
                </h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label fw-bold">Permanent Address *</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <Home size={16} />
                            </span>
                            <textarea
                                name="permanentAddress"
                                value={formData.permanentAddress}
                                onChange={handleChange}
                                className={`form-control ${errors.permanentAddress ? 'is-invalid' : ''}`}
                                placeholder="Enter permanent address"
                                rows="3"
                            />
                            {errors.permanentAddress && <div className="invalid-feedback">{errors.permanentAddress}</div>}
                        </div>
                    </div>

                    <div className="col-12 mb-3">
                        <label className="form-label fw-bold">Local Address</label>
                        <div className="input-group">
                            <span className="input-group-text">
                                <MapPin size={16} />
                            </span>
                            <textarea
                                name="localAddress"
                                value={formData.localAddress}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Enter local address (if different from permanent)"
                                rows="3"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderBankDetails = () => (
        <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
                <h5 className="mb-0 d-flex align-items-center">
                    <CreditCard size={20} className="me-2" />
                    Bank Information
                </h5>
            </div>
            <div className="card-body">
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Bank details are optional but recommended for payment processing.
                </div>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Account Holder Name</label>
                        <input
                            type="text"
                            name="bankDetails.accountHolderName"
                            value={formData.bankDetails.accountHolderName}
                            onChange={handleChange}
                            className={`form-control ${errors['bankDetails.accountHolderName'] ? 'is-invalid' : ''}`}
                            placeholder="Enter account holder name"
                        />
                        {errors['bankDetails.accountHolderName'] && <div className="invalid-feedback">{errors['bankDetails.accountHolderName']}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Bank Name</label>
                        <input
                            type="text"
                            name="bankDetails.bankName"
                            value={formData.bankDetails.bankName}
                            onChange={handleChange}
                            className={`form-control ${errors['bankDetails.bankName'] ? 'is-invalid' : ''}`}
                            placeholder="Enter bank name"
                        />
                        {errors['bankDetails.bankName'] && <div className="invalid-feedback">{errors['bankDetails.bankName']}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Account Number</label>
                        <input
                            type="text"
                            name="bankDetails.accountNumber"
                            value={formData.bankDetails.accountNumber}
                            onChange={handleChange}
                            className={`form-control ${errors['bankDetails.accountNumber'] ? 'is-invalid' : ''}`}
                            placeholder="Enter account number"
                        />
                        {errors['bankDetails.accountNumber'] && <div className="invalid-feedback">{errors['bankDetails.accountNumber']}</div>}
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">IFSC Code</label>
                        <input
                            type="text"
                            name="bankDetails.ifscCode"
                            value={formData.bankDetails.ifscCode}
                            onChange={handleChange}
                            className={`form-control ${errors['bankDetails.ifscCode'] ? 'is-invalid' : ''}`}
                            placeholder="Enter IFSC code"
                        />
                        {errors['bankDetails.ifscCode'] && <div className="invalid-feedback">{errors['bankDetails.ifscCode']}</div>}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDocumentUpload = () => {
        const documentTypes = [
            { key: 'agentPhoto', label: 'Agent Photo', required: false },
            { key: 'drivingLicenseFront', label: 'Driving License (Front)', required: true },
            { key: 'drivingLicenseBack', label: 'Driving License (Back)', required: true },
            { key: 'aadhaarCardFront', label: 'Aadhaar Card (Front)', required: true },
            { key: 'aadhaarCardBack', label: 'Aadhaar Card (Back)', required: true }
        ];

        return (
            <div className="card shadow-sm">
                <div className="card-header bg-warning text-dark">
                    <h5 className="mb-0 d-flex align-items-center">
                        <FileText size={20} className="me-2" />
                        Document Upload
                    </h5>
                </div>
                <div className="card-body">
                    <div className="alert alert-warning">
                        <i className="fas fa-upload me-2"></i>
                        Please upload clear, readable images of the required documents. Accepted formats: JPEG, PNG, PDF (max 5MB each).
                    </div>
                    <div className="row">
                        {documentTypes.map((docType) => (
                            <div key={docType.key} className="col-md-6 mb-4">
                                <div className="border rounded p-3 document-upload-card">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label fw-bold mb-0">
                                            {docType.label}
                                            {docType.required && <span className="text-danger ms-1">*</span>}
                                        </label>
                                        {formData.documents[docType.key] && (
                                            <span className="badge bg-success">
                                                <Check size={12} className="me-1" />
                                                Uploaded
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Document Preview */}
                                    {documentPreviews[docType.key] && (
                                        <div className="mb-3 text-center">
                                            <div className="position-relative d-inline-block">
                                                <img 
                                                    src={documentPreviews[docType.key]} 
                                                    alt={docType.label}
                                                    className="img-thumbnail border-2 cursor-pointer"
                                                    style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'cover' }}
                                                    onClick={() => window.open(documentPreviews[docType.key], '_blank')}
                                                    title="Click to view full size"
                                                />
                                                <div className="position-absolute top-0 end-0 p-1">
                                                    <span className="badge bg-success">
                                                        <Check size={12} />
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-2">
                                                <small className="text-muted d-block">Click to view full size</small>
                                            </div>
                                        </div>
                                    )}

                                    {/* File Input */}
                                    <div className="input-group">
                                        <input
                                            type="file"
                                            accept="image/*,.pdf"
                                            onChange={(e) => handleFileUpload(e, docType.key)}
                                            className={`form-control ${errors[docType.key] ? 'is-invalid' : ''}`}
                                            disabled={uploadingFiles[docType.key]}
                                        />
                                        <span className="input-group-text">
                                            {uploadingFiles[docType.key] ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                <Upload size={16} />
                                            )}
                                        </span>
                                        {errors[docType.key] && <div className="invalid-feedback">{errors[docType.key]}</div>}
                                    </div>

                                    {/* Document Status */}
                                    {formData.documents[docType.key] && (
                                        <div className="mt-2 d-flex justify-content-between align-items-center">
                                            <small className="text-success">
                                                <Check size={12} className="me-1" />
                                                {formData.documents[docType.key].split('/').pop()}
                                            </small>
                                            {formData.documents[docType.key] && (
                                                <a 
                                                    href={`${process.env.REACT_APP_SERVER_PUBLIC_PATH}/${formData.documents[docType.key]}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-primary btn-sm"
                                                >
                                                    <Camera size={12} className="me-1" />
                                                    View
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderWorkingHours = () => {
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const dayLabels = {
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday',
            saturday: 'Saturday',
            sunday: 'Sunday'
        };

        return (
            <div className="card shadow-sm">
                <div className="card-header bg-secondary text-white">
                    <h5 className="mb-0 d-flex align-items-center">
                        <Clock size={20} className="me-2" />
                        Working Hours
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        {days.map((day) => (
                            <div key={day} className="col-md-6 mb-3">
                                <div className="card border">
                                    <div className="card-body py-3">
                                        <h6 className="card-title mb-3">{dayLabels[day]}</h6>
                                        <div className="row">
                                            <div className="col-6">
                                                <label className="form-label small">Start Time</label>
                                                <input
                                                    type="time"
                                                    value={formData.workingHours[day].start}
                                                    onChange={(e) => handleWorkingHoursChange(day, 'start', e.target.value)}
                                                    className="form-control form-control-sm"
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="form-label small">End Time</label>
                                                <input
                                                    type="time"
                                                    value={formData.workingHours[day].end}
                                                    onChange={(e) => handleWorkingHoursChange(day, 'end', e.target.value)}
                                                    className="form-control form-control-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return renderPersonalInformation();
            case 2:
                return renderAddressDetails();
            case 3:
                return renderBankDetails();
            case 4:
                return renderDocumentUpload();
            case 5:
                return renderWorkingHours();
            default:
                return renderPersonalInformation();
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading agent details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/agents"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Agents
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewAgent ? 'Agent Onboarding' : `Edit Agent - ${formData.name}`}
                            </h2>
                            <div className="badge bg-primary fs-6">
                                Step {currentStep} of {totalSteps}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step Indicator */}
                {renderStepIndicator()}

                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Current Step Content */}
                    <div className="row mb-4">
                        <div className="col-12">
                            {renderCurrentStep()}
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-between">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="btn btn-outline-secondary"
                                    disabled={currentStep === 1}
                                >
                                    <ArrowLeft size={16} className="me-2" />
                                    Previous
                                </button>

                                <div>
                                    {currentStep < totalSteps ? (
                                        <button
                                            type="button"
                                            onClick={nextStep}
                                            className="btn btn-primary"
                                        >
                                            Next
                                            <ChevronRight size={16} className="ms-2" />
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="btn btn-success btn-lg"
                                            disabled={isSubmitting}
                                            onClick={onSubmit} // Explicitly call onSubmit only on the final step
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    {isNewAgent ? 'Creating Agent...' : 'Updating Agent...'}
                                                </>
                                            ) : (
                                                <>
                                                    <Save size={16} className="me-2" />
                                                    {isNewAgent ? 'Complete Onboarding' : 'Update Agent'}
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Custom CSS */}
            <style>{`
                .step-indicator {
                    display: flex;
                    align-items: center;
                    opacity: 0.5;
                    transition: opacity 0.3s ease;
                }

                .step-indicator.active {
                    opacity: 1;
                }

                .step-indicator.completed {
                    opacity: 1;
                }

                .step-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: #e9ecef;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-right: 12px;
                    transition: background-color 0.3s ease;
                }

                .step-indicator.active .step-circle {
                    background-color: #0d6efd;
                    color: white;
                }

                .step-indicator.completed .step-circle {
                    background-color: #198754;
                    color: white;
                }

                .step-info {
                    text-align: left;
                }

                .step-title {
                    font-weight: 600;
                    font-size: 0.9rem;
                    margin-bottom: 2px;
                }

                .step-description {
                    font-size: 0.75rem;
                    color: #6c757d;
                }

                .step-separator {
                    margin: 0 20px;
                    color: #dee2e6;
                }

                .document-upload-card {
                    background-color: #f8f9fa;
                    transition: border-color 0.3s ease;
                }

                .document-upload-card:hover {
                    border-color: #0d6efd !important;
                }

                .cursor-pointer {
                    cursor: pointer;
                }

                .cursor-pointer:hover {
                    opacity: 0.8;
                    transition: opacity 0.3s ease;
                }

                @media (max-width: 768px) {
                    .step-indicator {
                        flex-direction: column;
                        text-align: center;
                        margin-bottom: 20px;
                    }

                    .step-separator {
                        display: none;
                    }

                    .step-info {
                        text-align: center;
                        margin-top: 8px;
                    }
                }
            `}</style>
        </Fragment>
    );
};

AgentOnboarding.propTypes = {
    createAgent: PropTypes.func.isRequired,
    getAgent: PropTypes.func.isRequired,
    updateAgent: PropTypes.func.isRequired,
    clearAgent: PropTypes.func.isRequired,
    uploadAgentDocument: PropTypes.func.isRequired,
    agent: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    agent: state.agent.agent,
    loading: state.agent.loading
});

export default connect(mapStateToProps, {
    createAgent,
    getAgent,
    updateAgent,
    clearAgent,
    uploadAgentDocument
})(AgentOnboarding);
